import { Chart, ChartConfiguration, ChartData, ChartDataset, DefaultDataPoint, TooltipItem } from 'chart.js';
import { PropType, defineComponent, shallowRef } from 'vue';
import moment, { Moment } from 'moment';
import { NSpin } from 'naive-ui';

import { config } from './chart.config';
import componentBase from '~/components/component-base';

import { TimeSlot } from './time-slot.model';
import { AvoltaAlarmConfiguration } from '~/configs/alarms/avolta-alarm.config';
import { StationEvent } from '~/api/models/station-event.model';
import { Station } from '~/api/models/station.model';
import { AvoltaAlarmEnum } from '~/configs/alarms/avolta-alarm.model';
import { getAlarm } from '~/misc/events-utils';

export default defineComponent({
    name: 'chart-station-events',

    extends: componentBase,

    components: {
        NSpin,
    },

    props: {
        station: { type: Object as PropType<Station> },

        events: { type: Object as PropType<StationEvent[]>},
        since: { type: Object as PropType<Moment>},
        before: { type: Object as PropType<Moment>},
    },

    data() {
        return {

            loading: false,

            chart: null,
            datasets: [] as ChartDataset<'bar', DefaultDataPoint<'bar'>>[],

            slots: [] as TimeSlot[],

            AvoltaAlarmConfiguration,
            filters: {
                CH4_battery_alarm: true,
                CH4_concentration_alarm: true,
                CH4_system_failure: true,
                CH4_eol_alarm: true,
                offline: true
            } as {[key in AvoltaAlarmEnum]: boolean},
        }
    },

    computed: {

        /**
         * Time duration of slot in seconds
         * @returns 
         */
        step(): number {
            const duration = this.before.diff(this.since, 'days');
            if(duration <= 7) {
                return  8 * 3600;
            } else if (duration <= 2 * 30) {
                return 24 * 3600;
            } else {
                return 7 * 24 * 3600;
            }
        },

        nbSlots(): number {
            const duration = this.before.diff(this.since, 'days');
            return Math.floor(duration / this.step);
        },
    },

    async mounted() {
        this.init();
    },

    methods: {
        init() {
            const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;

            if (!chartElement || !chartElement.getContext('2d')) {
                return;
            }

            const context: CanvasRenderingContext2D | null = chartElement.getContext('2d');

            if(!context) {
                return;
            }

            this.compute();
            const configuration: ChartConfiguration<'bar', DefaultDataPoint<'bar'>> = { 
                ...config, 
                data: { labels: this.slots.map((ts: TimeSlot) => ts.start.format('LLL')), datasets: this.datasets }
            };
            
            configuration.options.plugins = {
                ...configuration.options.plugins,
                tooltip: {
                    callbacks: {
                        title: (items: TooltipItem<'bar'>[]) => {

                            if(!items?.length) {
                                return '';
                            }

                            const slot: TimeSlot = this.slots[items[0].dataIndex];

                            if(!slot) {
                                return items[0].label;
                            }

                            const format = slot.end.diff(slot.start, 'days') >= 1 ? 'LL' : 'LLL';

                            return `${slot.start.format(format)} - ${slot.end.format(format)}`
                        }
                    }
                }
            };

            //@ts-ignore
            this.chart = shallowRef(new Chart(context, configuration));
        },

        buildTimeslots(): TimeSlot[] {

            let current: Moment = this.before.clone();
            const result: TimeSlot[] = [];
            while(current.isAfter(this.since)) {
                const start: Moment = current.clone().subtract(8, 'hours');
                const end: Moment = current.clone();
                result.push({
                    start,
                    end,
                    battery: 0,
                    ch4Count: 0,
                    offline: 0,
                    system_failure: 0,
                    eol_count: 0,
                });
                current = start;
            }

            return result.reverse();
      },

        compute(): void {
            this.slots = this.buildTimeslots();

            const events: StationEvent[] = this.events ? this.events : [];

            const datasets: {[key in AvoltaAlarmEnum]: ChartDataset<'bar'>} = {
                [AvoltaAlarmEnum.CH4_concentration_alarm]: null,
                [AvoltaAlarmEnum.CH4_system_failure]: null,
                [AvoltaAlarmEnum.CH4_battery_alarm]: null,
                [AvoltaAlarmEnum.CH4_eol_alarm]: null,
                [AvoltaAlarmEnum.offline]: null
            };

            // Init datasets
            const ch4AlarmConfig = AvoltaAlarmConfiguration[AvoltaAlarmEnum.CH4_concentration_alarm];
            datasets[AvoltaAlarmEnum.CH4_concentration_alarm] = ({ label: ch4AlarmConfig.short, data: [], backgroundColor: ch4AlarmConfig.color });

            const batteryAlarmConfig = AvoltaAlarmConfiguration[AvoltaAlarmEnum.CH4_battery_alarm];
            datasets[AvoltaAlarmEnum.CH4_battery_alarm] = ({ label: batteryAlarmConfig.short, data: [], backgroundColor: batteryAlarmConfig.color });

            const systemFailureAlarm = AvoltaAlarmConfiguration[AvoltaAlarmEnum.CH4_system_failure];
            datasets[AvoltaAlarmEnum.CH4_system_failure] = ({ label: systemFailureAlarm.short, data: [], backgroundColor: systemFailureAlarm.color });

            const offlineAlarm = AvoltaAlarmConfiguration[AvoltaAlarmEnum.offline];
            datasets[AvoltaAlarmEnum.offline] = ({ label: offlineAlarm.short, data: [], backgroundColor: offlineAlarm.color });

            const eolAlarm = AvoltaAlarmConfiguration[AvoltaAlarmEnum.CH4_eol_alarm];
            datasets[AvoltaAlarmEnum.CH4_eol_alarm] = ({ label: eolAlarm.short, data: [], backgroundColor: eolAlarm.color });

            for(let i = 0; i < this.slots.length; i++) {
                const slot: TimeSlot = this.slots[i];

                events
                    .filter(e => e?.level === 'warning')
                    .filter((event: StationEvent) => moment.unix(event.date).isBetween(slot.start, slot.end))
                    .sort((e1,e2) => e1?.date - e2?.date)
                    .forEach(event => {
                        const alarm = getAlarm(event);

                                
                        if(alarm === AvoltaAlarmEnum.CH4_concentration_alarm) {
                            this.slots[i].ch4Count++;
                        } 
                        
                        if(alarm === AvoltaAlarmEnum.CH4_battery_alarm) {
                            this.slots[i].battery++;
                        }

                        if(alarm === AvoltaAlarmEnum.CH4_system_failure) {
                            this.slots[i].system_failure++;
                        }

                        if(alarm === AvoltaAlarmEnum.CH4_eol_alarm) {
                            this.slots[i].eol_count++;
                        }

                        if(alarm === AvoltaAlarmEnum.offline) {
                            this.slots[i].offline++;
                        }
                    });

                
            }

            this.slots.forEach((slot: TimeSlot) => {
                datasets[AvoltaAlarmEnum.CH4_concentration_alarm]?.data.push(slot.ch4Count);
                datasets[AvoltaAlarmEnum.CH4_battery_alarm]?.data.push(slot.battery);
                datasets[AvoltaAlarmEnum.CH4_system_failure]?.data.push(slot.system_failure);
                datasets[AvoltaAlarmEnum.offline]?.data.push(slot.offline);
            });

            this.datasets = Object.values(datasets).filter(d => d);
        },

        updateChart() {
 
            if (!this.chart) {
                return;
            }

            try {
                this.loading = true;
                this.compute();
                this.chart.data = {
                    labels: this.slots.map((ts: TimeSlot) => ts.start.format('l')),
                    datasets: this.datasets
                } as ChartData;

                this.chart.options.scales.x.ticks.autoSkip = true;
                this.chart.update();
                this.loading = false;
            } catch (e) {
                console.error(e);
                this.$notifyError(`An error occured while updating chart options ${this.chart.options}`, e);
            } finally {
                this.loading = false;
            }
        },

        onFilter(alarm: AvoltaAlarmEnum): void {
            this.filters[alarm] = !this.filters[alarm];
            this.updateChart();
        },
    },

    watch: {
        events(events: StationEvent[]) {
            if(!events) {
                this.loading = true;
            } else {
                this.loading = false;
                this.updateChart();
            }
        }
    }

});
